import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/base/SEO';
import './styles.css';

export default function HomePage() {
  const { t } = useTranslation(['home', 'common']);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEO
        title={t('home:seo.title')}
        description={t('home:seo.description')}
        keywords={t('home:seo.keywords')}
        ogImage="https://public.readdy.ai/ai/img_res/edited_f375a58d5fdb51297a04fea8cade9183_ffb311e9.jpg"
      />
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_f375a58d5fdb51297a04fea8cade9183_ffb311e9.jpg"
            alt={t('home:hero.imageAlt')}
            width="1920"
            height="1080"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <h1 className="hero-title text-white mb-6 sm:mb-8 leading-tight text-2xl sm:text-4xl md:text-5xl lg:text-6xl">
            {t('home:hero.title')}
          </h1>
          <p className="hero-subtitle text-white/95 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed text-sm sm:text-base lg:text-lg px-2">
            {t('home:hero.subtitle')}
          </p>
          <Link
            to="/payment"
            className="inline-block px-7 sm:px-12 py-3 sm:py-4 bg-[#8B9D83] text-white text-xs sm:text-sm font-light tracking-widest uppercase rounded-sm hover:bg-[#7A8C72] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            aria-label={t('home:hero.ctaAria')}
          >
            {t('home:hero.cta')}
          </Link>
        </div>

        <div className="absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 z-10" style={{ willChange: 'transform' }}>
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Why Two Experts Section */}
      <section className="py-12 sm:py-24 lg:py-32 px-5 sm:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-20">
            <h2 className="section-title text-[#4A5D44] mb-6 text-xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight">
              {t('home:whyTwo.title')}
            </h2>
            <div className="w-16 sm:w-24 h-px bg-[#8B9D83] mx-auto mb-6 sm:mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/9e76b61bc6d76a2608c65f0deb07c81b.jpg"
                alt={t('home:whyTwo.imageAlt')}
                width="800"
                height="900"
                loading="lazy"
                decoding="async"
                className="w-full h-56 sm:h-96 md:h-[500px] object-cover rounded-sm shadow-lg"
              />
            </div>
            <div className="space-y-5 sm:space-y-8">
              <p className="text-sm sm:text-base leading-relaxed text-gray-700 font-light">
                {t('home:whyTwo.description')}
              </p>
              <div className="bg-[#F5F7F3] p-5 sm:p-8 rounded-sm">
                <p className="text-sm sm:text-base leading-relaxed text-[#4A5D44] font-light italic">
                  {t('home:whyTwo.quote')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:gap-6 pt-2 sm:pt-4">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-light text-[#8B9D83] mb-1 sm:mb-2">2</div>
                  <div className="text-xs sm:text-sm text-gray-600 font-light">{t('home:whyTwo.experts')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-light text-[#8B9D83] mb-1 sm:mb-2">×10</div>
                  <div className="text-xs sm:text-sm text-gray-600 font-light">{t('home:whyTwo.result')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-12 sm:py-24 lg:py-32 px-5 sm:px-8 bg-[#F5F7F3]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-title text-[#4A5D44] mb-6 sm:mb-12 text-xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight">
            {t('home:philosophy.title')}
          </h2>
          <div className="w-16 sm:w-24 h-px bg-[#8B9D83] mx-auto mb-6 sm:mb-12"></div>
          <p className="text-sm sm:text-base lg:text-lg leading-relaxed text-gray-700 font-light mb-5 sm:mb-8">
            {t('home:philosophy.belief')}
          </p>
          <p className="text-sm sm:text-base leading-relaxed text-gray-600 font-light max-w-3xl mx-auto">
            {t('home:philosophy.description')}
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-24 lg:py-32 px-5 sm:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-[#4A5D44] mb-5 sm:mb-8 leading-tight">
            {t('home:cta.title')}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mt-6 sm:mt-12">
            <Link
              to="/payment"
              className="w-full sm:w-auto inline-block text-center px-7 sm:px-12 py-3 sm:py-4 bg-[#8B9D83] text-white text-xs sm:text-sm font-light tracking-widest uppercase rounded-sm hover:bg-[#7A8C72] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              aria-label={t('home:cta.primaryAria')}
            >
              {t('home:cta.primary')}
            </Link>
            <Link
              to="/payment"
              className="w-full sm:w-auto inline-block text-center px-7 sm:px-12 py-3 sm:py-4 bg-transparent border-2 border-[#8B9D83] text-[#8B9D83] text-xs sm:text-sm font-light tracking-widest uppercase rounded-sm hover:bg-[#8B9D83] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              aria-label={t('home:cta.secondaryAria')}
            >
              {t('home:cta.secondary')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}